import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useParams } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { BaseButton } from 'iebt-zing-storybook'
import { deleteProvider, GetFormat, useGet } from 'sharedKernel/api'
import { api } from 'sharedKernel/api/config'
import { getHubRoute } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { trans } from 'sharedKernel/i18n'
import { Loading } from 'components/loading'
import { Filter } from 'components/Filter'
import { FilterOrderBar } from 'sharedKernel/styledComponents/filterComponents/style'
import { showModal } from 'components/popup'
import { showToast } from 'components/toast'
import {
  MEETING_ROOM_API,
} from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/constants'
import { normalizeMeetingRoomDetailResponse } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/mappers'
import { MeetingRoomReservationsTable } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/MeetingRoomReservationsTable'
import { IMeetingRoomReservationRow } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/MeetingRoomReservationsTable/types'
import { mapSolicitationToReservationDetail } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/api/mappers'
import { EditReservationModal } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/EditReservationModal'
import { useMeetingRoomSpaceOptions } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/hooks/useMeetingRoomSpaceOptions'
import {
  MEETING_ROOM_SOLICITATIONS_PAGE_SIZE,
} from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/api/reservationConstants'
import { ListPaginationFooter } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/ListPaginationFooter'
import {
  useMeetingRoomSolicitationDetail,
  useMeetingRoomSolicitationActions,
  useMeetingRoomSolicitations,
} from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/hooks/useMeetingRoomSolicitations'
import { SolicitationDetailModal } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/SolicitationDetailModal'
import {
  DetailActions,
  HideSpaceButton,
  MeetingRoomToolbarActions,
  PageHeader,
  PageTitle,
} from './detailStyles'

type SolicitationAction = 'approve' | 'reject' | 'cancel'

const CONFIRMATION_CONFIG: Record<
  SolicitationAction,
  { titleKey: string; messageKey: string; toastKey: string }
> = {
  approve: {
    titleKey: 'meetingRoom.reservations.confirm.approveTitle',
    messageKey: 'meetingRoom.reservations.confirm.approveMessage',
    toastKey: 'meetingRoom.reservations.toast.approveSuccess',
  },
  reject: {
    titleKey: 'meetingRoom.reservations.confirm.rejectTitle',
    messageKey: 'meetingRoom.reservations.confirm.rejectMessage',
    toastKey: 'meetingRoom.reservations.toast.rejectSuccess',
  },
  cancel: {
    titleKey: 'meetingRoom.reservations.confirm.cancelTitle',
    messageKey: 'meetingRoom.reservations.confirm.cancelMessage',
    toastKey: 'meetingRoom.reservations.toast.cancelSuccess',
  },
}

export const HubCompanyMeetingRoomDetailPage = (): JSX.Element => {
  const { meetingRoomId } = useParams()
  const navigate = useNavigate()
  const [isChangingVisibility, setIsChangingVisibility] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null)
  const [editingSolicitationId, setEditingSolicitationId] = useState<string | null>(null)

  const pageSize = MEETING_ROOM_SOLICITATIONS_PAGE_SIZE

  const request: GetFormat = {
    url: MEETING_ROOM_API.byId(meetingRoomId ?? ''),
  }

  const { data, isFetching, isError, refetch } = useGet<unknown>(
    request,
    `meeting-room-detail-${meetingRoomId ?? ''}`,
    { enabled: !!meetingRoomId }
  )

  const {
    solicitations,
    totalRows,
    isLoading: isReservationsLoading,
    refetch: refetchReservations,
  } = useMeetingRoomSolicitations({
    searchTerm: appliedSearchTerm,
    meetingRoomId,
    page: currentPage,
    pageSize,
  })

  const { solicitation: selectedReservationDetail } =
    useMeetingRoomSolicitationDetail(selectedReservationId)

  const { solicitation: editingSolicitationDetail, isLoading: isEditingDetailLoading } =
    useMeetingRoomSolicitationDetail(editingSolicitationId)

  const editingSolicitation = useMemo(() => {
    if (editingSolicitationDetail) {
      return editingSolicitationDetail
    }

    if (!editingSolicitationId) {
      return null
    }

    return solicitations.find(item => item.id === editingSolicitationId) ?? null
  }, [editingSolicitationDetail, editingSolicitationId, solicitations])

  const { spaceOptions, isLoading: isSpaceOptionsLoading } = useMeetingRoomSpaceOptions(
    editingSolicitation?.meetingRoomId ?? meetingRoomId
  )

  const room = useMemo(() => normalizeMeetingRoomDetailResponse(data), [data])

  const reservations = useMemo(
    () => solicitations.map(mapSolicitationToReservationDetail),
    [solicitations]
  )

  const selectedReservation = useMemo(() => {
    if (selectedReservationDetail) {
      return mapSolicitationToReservationDetail(selectedReservationDetail)
    }

    if (!selectedReservationId) {
      return null
    }

    return reservations.find(item => item.id === selectedReservationId) ?? null
  }, [selectedReservationDetail, selectedReservationId, reservations])

  const { approve, reject, cancel, edit, exportReservations, isUpdating, isExporting } =
    useMeetingRoomSolicitationActions(refetchReservations)

  const runAction = async (
    action: SolicitationAction,
    row: IMeetingRoomReservationRow
  ): Promise<void> => {
    const solicitation = solicitations.find(item => item.id === row.id)
    if (!solicitation) return

    try {
      if (action === 'approve') {
        await approve(solicitation)
      } else if (action === 'reject') {
        await reject(solicitation)
      } else {
        await cancel(solicitation)
      }

      showToast('success', trans(CONFIRMATION_CONFIG[action].toastKey))
    } catch {
      showToast('error', trans('meetingRoom.reservations.toast.actionError'))
    }
  }

  const confirmSolicitationAction = (
    action: SolicitationAction,
    row: IMeetingRoomReservationRow
  ): void => {
    const config = CONFIRMATION_CONFIG[action]

    showModal('confirmation', {
      title: trans(config.titleKey),
      message: trans(config.messageKey),
      onConfirm: () => {
        void runAction(action, row)
      },
    })
  }

  const handleConfirmEdit = async (
    solicitationId: string,
    data: { meetingRoomId: string; roomName: string; startTime: string; endTime: string }
  ): Promise<void> => {
    const solicitation =
      editingSolicitation ??
      solicitations.find(item => item.id === solicitationId) ??
      null
    if (!solicitation) return

    try {
      await edit(solicitation, data)
      showToast('success', trans('meetingRoom.reservations.toast.editSuccess'))
      setEditingSolicitationId(null)
    } catch {
      showToast('error', trans('meetingRoom.reservations.toast.actionError'))
    }
  }

  const { mutate: deleteMeetingRoom } = deleteProvider(async () => {
    showToast('success', trans('meetingRoom.toast.deleteSuccess'))
    navigate(getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE))
  })

  const handleEdit = (): void => {
    if (!meetingRoomId) return
    navigate(
      getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_EDIT).replace(
        ':meetingRoomId',
        meetingRoomId
      )
    )
  }

  const handleDelete = (): void => {
    if (!meetingRoomId || !room) return

    showModal('double', {
      title: trans('meetingRoom.list.deleteTitle'),
      message: `${trans('meetingRoom.list.deleteMessagePrefix')} "${room.name}"?`,
      buttons: [
        { text: trans('meetingRoom.common.yes'), value: 'yes' },
        { text: trans('meetingRoom.common.no'), value: 'no' },
      ],
      action: () => {
        deleteMeetingRoom({ url: MEETING_ROOM_API.byId(meetingRoomId) })
      },
    })
  }

  const handleToggleHidden = async (): Promise<void> => {
    if (!meetingRoomId || !room) return

    try {
      setIsChangingVisibility(true)
      await api.put(
        room.isHidden
          ? MEETING_ROOM_API.unhide(meetingRoomId)
          : MEETING_ROOM_API.hide(meetingRoomId)
      )
      showToast(
        'success',
        room.isHidden
          ? trans('meetingRoom.toast.unhideSuccess')
          : trans('meetingRoom.toast.hideSuccess')
      )
      await refetch()
    } catch {
      showToast('error', trans('meetingRoom.toast.visibilityError'))
    } finally {
      setIsChangingVisibility(false)
    }
  }

  const handleSearch = (term: string): void => {
    setSearchTerm(term)
    setAppliedSearchTerm(term)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number): void => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (isFetching) {
    return <Loading />
  }

  if (isError || !room) {
    return (
      <div className="p-4 text-center text-muted">
        {trans('meetingRoom.edit.loadError')}
      </div>
    )
  }

  return (
    <>
      <PageHeader>
        <PageTitle>{room.name}</PageTitle>
        <DetailActions>
          <HideSpaceButton
            variant="light"
            disabled={isChangingVisibility}
            onClick={handleToggleHidden}
          >
            <FontAwesomeIcon icon={['fas', 'eye']} />
            {room.isHidden
              ? trans('meetingRoom.detail.unhideSpace')
              : trans('meetingRoom.detail.hideSpace')}
          </HideSpaceButton>
          <BaseButton
            btnContent={<FontAwesomeIcon icon={['fas', 'pen']} size="xl" />}
            variant="primary-invert"
            size="icon"
            tippyContent={trans('meetingRoom.list.edit')}
            onClick={handleEdit}
          />
          <BaseButton
            btnContent={<FontAwesomeIcon icon={['fas', 'trash']} size="xl" />}
            variant="primary-invert"
            size="icon"
            tippyContent={trans('meetingRoom.list.delete')}
            onClick={handleDelete}
          />
        </DetailActions>
      </PageHeader>

      <FilterOrderBar>
        <Filter
          currentSearchTerm={searchTerm}
          btnClick={handleSearch}
          onFilterChange={setSearchTerm}
          minSearchTermLength={1}
        />
        <MeetingRoomToolbarActions>
          <BaseButton
            variant="primary-invert"
            disabled={isExporting}
            onClick={() => { void exportReservations(meetingRoomId) }}
            btnContent={
              <span>
                <FontAwesomeIcon className="pr-2" icon={['fas', 'download']} />
                {trans('meetingRoom.detail.export')}
              </span>
            }
          />
        </MeetingRoomToolbarActions>
      </FilterOrderBar>

      {isReservationsLoading ? (
        <Loading />
      ) : (
        <>
          <MeetingRoomReservationsTable
            rows={reservations}
            searchTerm=""
            onViewDetails={row => setSelectedReservationId(row.id)}
            onEdit={row => setEditingSolicitationId(row.id)}
            onApprove={row => confirmSolicitationAction('approve', row)}
            onReject={row => confirmSolicitationAction('reject', row)}
            onCancel={row => confirmSolicitationAction('cancel', row)}
          />
          <ListPaginationFooter
            currentPage={currentPage}
            pageSize={pageSize}
            totalRows={totalRows}
            onPageChange={handlePageChange}
          />
        </>
      )}

      <EditReservationModal
        isOpen={editingSolicitationId !== null}
        solicitation={editingSolicitation}
        spaceOptions={spaceOptions}
        isSubmitting={isUpdating}
        isLoading={isEditingDetailLoading || isSpaceOptionsLoading}
        onClose={() => setEditingSolicitationId(null)}
        onConfirm={(solicitationId, data) => {
          void handleConfirmEdit(solicitationId, data)
        }}
      />

      <SolicitationDetailModal
        solicitation={selectedReservation}
        onClose={() => setSelectedReservationId(null)}
      />
    </>
  )
}
